import { SIMBOLOGIA, TEXTOS } from '../../manual-textos';
import { formatarData, itensValidos, ou } from '../../manual-utils';
import {
  blocoAlerta,
  blocoCapitulo,
  blocoLista,
  blocoNota,
  blocoSecao,
  blocoTabela,
  blocoTexto,
} from '../blocos';
import type { Bloco } from '../blocos';
import type { ManualData } from '../../manual-types';

export function capitulo1(dados: ManualData): Bloco[] {
  const blocos: Bloco[] = [blocoCapitulo(1, 'Designações gerais', false)];

  blocos.push(...blocoTexto(
    'Neste capítulo são apresentados os dados gerais da máquina, da reconstituidora e da empresa contratante, além das informações do levantamento técnico que originou este documento.',
    'c1-abertura',
  ));

  blocos.push(...blocoTabela({
    id: 'c1-levantamento',
    legenda: 'LEVANTAMENTO DE DADOS PARA RECONSTITUIÇÃO DESTE MANUAL',
    colunas: ['DATA', 'LOCAL', 'RESPONSÁVEL TÉCNICO'],
    linhas: [[
      formatarData(dados.dataLevantamento, 'N/I'),
      ou(dados.localLevantamento),
      ou(dados.responsavelLevantamento),
    ]],
  }));

  blocos.push(...blocoTabela({
    id: 'c1-designacao',
    legenda: 'DESIGNAÇÃO DA MÁQUINA',
    colunas: ['ITEM', 'DESCRIÇÃO'],
    linhas: [
      ['Máquina', ou(dados.nomeMaquina, 'NOME DA MÁQUINA')],
      ['Identificação / TAG', ou(dados.identificacao)],
      ['Modelo / Tipo', ou(dados.modeloTipo)],
      ['Número de série', ou(dados.numeroSerie)],
      ['Ano de fabricação', ou(dados.anoFabricacao)],
      ['Código de rastreabilidade', ou(dados.codigoRastreabilidade)],
      ['Versão deste manual', ou(dados.versao, '0')],
      ['Data de emissão', formatarData(dados.dataEmissao, 'N/I')],
    ],
    larguras: ['36%'],
    alinharEsquerda: true,
  }));

  blocos.push(...blocoTabela({
    id: 'c1-empresas',
    legenda: 'FABRICANTE, RECONSTITUIDORA E CONTRATANTE',
    colunas: ['ITEM', 'DESCRIÇÃO'],
    linhas: [
      ['Fabricante original', ou(dados.fabricante)],
      ['Contato do fabricante', ou(dados.fabricanteContato)],
      ['Reconstituidora', ou(dados.opcoes.reconstituidora)],
      ['Contratante', ou(dados.contratante)],
      ['Unidade', ou(dados.contratanteUnidade)],
      ['Setor de instalação', ou(dados.setor)],
    ],
    larguras: ['36%'],
    alinharEsquerda: true,
  }));

  blocos.push(blocoSecao('1.1', 'Sobre este manual reconstituído'));
  blocos.push(...blocoTexto(TEXTOS.sobreManual, 'c1-sobre'));
  blocos.push(...blocoTexto(TEXTOS.sobreManualLimite, 'c1-sobre-limite'));

  blocos.push(blocoSecao('1.2', 'Materiais consultados'));
  blocos.push(...blocoTexto(TEXTOS.materiaisIntro, 'c1-materiais-intro'));
  const documentos = itensValidos(dados.documentosConsultados, ['texto']).map((item) => item.texto);
  blocos.push(...(documentos.length
    ? blocoLista(documentos, 'c1-materiais')
    : [blocoNota('Nenhum documento adicional foi disponibilizado pela contratante até a data do levantamento.', 'c1-materiais-vazio')]));

  if (dados.opcoes.incluirSimbologia) {
    blocos.push(blocoSecao('1.3', 'Simbologia'));
    blocos.push(...blocoTexto(TEXTOS.simbologiaIntro, 'c1-simbologia-intro'));
    SIMBOLOGIA.forEach((alerta, indice) => blocos.push(blocoAlerta(alerta, `c1-simbologia-${indice}`)));
  }

  blocos.push(blocoSecao('1.4', 'Público-alvo'));
  blocos.push(...blocoTexto(TEXTOS.publicoIntro, 'c1-publico-intro'));
  const publico = itensValidos(dados.publicoAlvo, ['texto']).map((item) => item.texto);
  blocos.push(...(publico.length
    ? blocoLista(publico, 'c1-publico')
    : [blocoNota('Público-alvo não informado no levantamento.', 'c1-publico-vazio')]));

  if (dados.opcoes.incluirNormas) {
    blocos.push(blocoSecao('1.5', 'Legislação e normas'));
    blocos.push(...blocoTexto(TEXTOS.normasIntro, 'c1-normas-intro'));
    const normas = itensValidos(dados.normas, ['codigo', 'titulo']);
    blocos.push(...(normas.length
      ? blocoTabela({
        id: 'c1-normas',
        colunas: ['DOCUMENTO', 'TÍTULO', 'ANO'],
        linhas: normas.map((norma) => [ou(norma.codigo, '—').replace(/-/g, '\u2011'), ou(norma.titulo, '—'), ou(norma.ano, '—')]),
        larguras: ['22%', '62%', '16%'],
        alinharEsquerda: true,
      })
      : [blocoNota('Nenhuma norma foi relacionada até a emissão desta versão.', 'c1-normas-vazio')]));
  }

  blocos.push(blocoSecao('1.6', 'Responsabilidades'));
  blocos.push(...blocoTexto(TEXTOS.responsabilidades, 'c1-resp-1'));
  blocos.push(...blocoTexto(TEXTOS.responsabilidadesOperador, 'c1-resp-2'));

  blocos.push(blocoSecao('1.7', 'Garantia'));
  blocos.push(...blocoTexto(TEXTOS.garantia, 'c1-gar-1'));
  blocos.push(...blocoTexto(TEXTOS.garantiaIsencao, 'c1-gar-2'));

  return blocos;
}
